import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { FaTimes, FaMoneyBillWave, FaSave, FaBuilding } from 'react-icons/fa';
import { toast } from 'react-toastify';
import styles from './FTEModal.module.css';

const FTEModal = ({ show, onClose, employee, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [projectFTEs, setProjectFTEs] = useState({});

  useEffect(() => {
    if (show && employee) {
      fetchEmployeeProjects();
      fetchTariffs();
    }
  }, [show, employee]);

  const fetchEmployeeProjects = async () => {
    if (!employee?.id) return;
    
    setLoading(true);
    try {
      // Fetch all projects where this employee is assigned
      const projectsRef = collection(db, 'Projects');
      const projectsSnapshot = await getDocs(projectsRef);
      
      const employeeProjects = [];
      const currentFTEs = {};
      
      projectsSnapshot.docs.forEach(projectDoc => {
        const projectData = projectDoc.data();
        const projectEmployees = projectData.employees || [];
        
        // Find this employee in the project
        const employeeInProject = projectEmployees.filter(emp => emp.id === employee.id);
        
        if (employeeInProject.length > 0) {
          employeeInProject.forEach(emp => {
            const projectInfo = {
              projectId: projectDoc.id,
              projectTitle: projectData.titleShort || projectData.title || 'N/A',
              centerName: emp.centerName || 'N/A',
              centerId: emp.centerId,
              role: emp.role,
              currentRate: emp.rate
            };
            
            employeeProjects.push(projectInfo);
            
            // Store current FTE if exists
            const key = `${projectDoc.id}-${emp.centerId}`;
            if (emp.rate && emp.rate.type?.toUpperCase() === 'FTE') {
              currentFTEs[key] = emp.rate.id || '';
            }
          });
        }
      });
      
      setProjects(employeeProjects);
      setProjectFTEs(currentFTEs);
    } catch (error) {
      console.error('Error fetching employee projects:', error);
      toast.error('Proje bilgileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchTariffs = async () => {
    try {
      const tariffsRef = collection(db, 'Tariffs');
      const tariffsSnapshot = await getDocs(tariffsRef);
      
      const tariffsData = tariffsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter only FTE tariffs
      const fteTariffsData = tariffsData.filter(t => t.type?.toUpperCase() === 'FTE');
      
      // Sort by value
      fteTariffsData.sort((a, b) => parseFloat(a.value) - parseFloat(b.value));
      
      setTariffs(fteTariffsData);
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error('Tarife bilgileri yüklenirken hata oluştu');
    }
  };

  const handleFTEChange = (projectId, centerId, tariffId) => {
    const key = `${projectId}-${centerId}`;
    setProjectFTEs({
      ...projectFTEs,
      [key]: tariffId
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Group projects by projectId
      const projectUpdates = {};
      
      projects.forEach(project => {
        const key = `${project.projectId}-${project.centerId}`;
        const selectedTariffId = projectFTEs[key];
        
        if (!projectUpdates[project.projectId]) {
          projectUpdates[project.projectId] = [];
        }
        
        projectUpdates[project.projectId].push({
          centerId: project.centerId,
          tariffId: selectedTariffId
        });
      });
      
      // Update each project
      for (const [projectId, updates] of Object.entries(projectUpdates)) {
        const projectRef = doc(db, 'Projects', projectId);
        const projectDoc = await getDocs(query(collection(db, 'Projects'), where('__name__', '==', projectId)));
        
        if (projectDoc.empty) continue;
        
        const projectData = projectDoc.docs[0].data();
        const employees = projectData.employees || [];
        
        // Update employee rates
        const updatedEmployees = employees.map(emp => {
          if (emp.id !== employee.id) return emp;
          
          // Find if this employee-center combination needs update
          const update = updates.find(u => u.centerId === emp.centerId);
          if (!update) return emp;
          
          // Find the selected tariff
          const selectedTariff = tariffs.find(t => t.id === update.tariffId);
          
          if (selectedTariff) {
            return {
              ...emp,
              rate: {
                id: selectedTariff.id,
                type: selectedTariff.type,
                value: selectedTariff.value,
                price: selectedTariff.price
              }
            };
          }
          
          return emp;
        });
        
        await updateDoc(projectRef, { employees: updatedEmployees });
      }
      
      if (onSaved) onSaved();
    } catch (error) {
      console.error('Error saving FTE:', error);
      toast.error('FTE bilgileri kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const formatTariffDisplay = (tariff) => {
    return `${tariff.type} - ${tariff.value}`;
  };

  if (!show) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FaMoneyBillWave className={styles.headerIcon} />
            <div>
              <div className={styles.headerTitle}>FTE Yönetimi</div>
              <div className={styles.headerSubtitle}>
                {employee?.name} - Proje Bazlı FTE Atamaları
              </div>
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p>Yükleniyor...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className={styles.emptyState}>
              <FaBuilding className={styles.emptyIcon} />
              <p>Bu çalışan için proje ataması bulunamadı</p>
            </div>
          ) : (
            <div className={styles.projectsList}>
              {projects.map((project, index) => {
                const key = `${project.projectId}-${project.centerId}`;
                const currentFTE = projectFTEs[key] || '';
                
                return (
                  <div key={index} className={styles.projectCard}>
                    <div className={styles.projectInfo}>
                      <div className={styles.projectTitle}>{project.projectTitle}</div>
                      <div className={styles.projectDetails}>
                        <span className={styles.projectBadge}>
                          <FaBuilding /> {project.centerName}
                        </span>
                        <span className={styles.roleBadge}>{project.role}</span>
                      </div>
                      {project.currentRate && (
                        <div className={styles.currentRate}>
                          Mevcut: {project.currentRate.type} - {project.currentRate.value}
                        </div>
                      )}
                    </div>
                    
                    <div className={styles.fteSelection}>
                      <label className={styles.fteLabel}>FTE Seçimi:</label>
                      <select
                        value={currentFTE}
                        onChange={(e) => handleFTEChange(project.projectId, project.centerId, e.target.value)}
                        className={styles.fteSelect}
                      >
                        <option value="">Seçiniz</option>
                        {tariffs
                          .filter(tariff => tariff.project === project.projectId)
                          .map(tariff => (
                            <option key={tariff.id} value={tariff.id}>
                              {formatTariffDisplay(tariff)}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose}>
            İptal
          </button>
          <button 
            className={styles.saveButton} 
            onClick={handleSave}
            disabled={saving || loading || projects.length === 0}
          >
            {saving ? (
              <>
                <div className={styles.spinner} style={{ width: 16, height: 16, borderWidth: 2 }}></div>
                Kaydediliyor...
              </>
            ) : (
              <>
                <FaSave /> Kaydet
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FTEModal;
